import { Identifier } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk, Draft } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { PaginatedResponse } from 'app/interfaces/api';
import { AffiliateProgramEntity, AffiliateProgramStatistics } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { store } from 'redux/store';
import { CreateAffiliateProgramFormFields, EditAffiliateProgramFormFields } from '../interfaces';
import { entityAdapter } from './adapter';
import { AffiliateProgramsStoreStatus } from './enums';
import { AffiliateProgramsState, GetAffiliateProgramStatisticsRequest } from './interfaces';

const saveAffiliatePrograms = (
  state: Draft<AffiliateProgramsState>,
  payload: PaginatedResponse<AffiliateProgramEntity[]>
) => {
  entityAdapter.setAll(state, payload.data);

  state.total = payload.total;
  state.offset = payload.data.length;
};

const saveAffiliateProgram = (
  state: Draft<AffiliateProgramsState>,
  payload: AffiliateProgramEntity | { statistics: AffiliateProgramStatistics; id: Identifier }
) => {
  const id = payload.id;

  if (state.ids.includes(id)) {
    entityAdapter.updateOne(state, { id, changes: payload });
  } else {
    entityAdapter.addOne(state, payload as AffiliateProgramEntity);
  }
};

const getPaginationParams = () => {
  const {
    limit = 1,
    offset = 0,
    reload = false,
  } = store.getState()[EntityStoreName.AffiliatePrograms];

  return { limit, offset, reload };
};

export const getAffiliatePrograms = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/getAffiliatePrograms`,
  (_, { signal }): Promise<PaginatedResponse<AffiliateProgramEntity[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const resource = ApiResource.AffiliatePrograms;

    return apiRequestCall<PaginatedResponse<AffiliateProgramEntity[]>>({
      params: { method: 'GET', resource, cancelToken },
    });
  }
);

export const getAdminAffiliatePrograms = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/getAdminAffiliatePrograms`,
  (_, { signal }): Promise<PaginatedResponse<AffiliateProgramEntity[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const urlPrefix = ApiResource.AffiliatePrograms;
    const resource = ApiResource.Admin;

    const { limit, offset, reload } = getPaginationParams();
    const params = {
      limit: reload ? Math.max(offset, limit) : limit,
      offset,
    };

    return apiRequestCall<PaginatedResponse<AffiliateProgramEntity[]>>({
      params: { method: 'GET', resource, urlPrefix, cancelToken, params },
    });
  }
);

export const getAffiliateProgram = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/getAffiliateProgram`,
  (id: Identifier, { signal }): Promise<AffiliateProgramEntity> => {
    const cancelToken = cancelationSubscribe(signal);
    const resource = ApiResource.AffiliatePrograms;

    return apiRequestCall<AffiliateProgramEntity>({
      params: { method: 'GET', resource, cancelToken, urlPrefix: id },
    });
  }
);

export const getAdminAffiliateProgram = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/getAdminAffiliateProgram`,
  (id: Identifier, { signal }): Promise<AffiliateProgramEntity> => {
    const cancelToken = cancelationSubscribe(signal);
    const urlPrefix = `${ApiResource.AffiliatePrograms}/${id}`;
    const resource = ApiResource.Admin;

    return apiRequestCall<AffiliateProgramEntity>({
      params: { method: 'GET', resource, cancelToken, urlPrefix },
    });
  }
);

export const getAffiliateProgramStatistics = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/getAdminAffiliateProgramStatistics`,
  (data: GetAffiliateProgramStatisticsRequest, { signal }): Promise<AffiliateProgramStatistics> => {
    const { id, startDate, endDate, type } = data;
    const params = { startDate, endDate };
    const cancelToken = cancelationSubscribe(signal);
    const isAdmin = type === 'admin';
    const urlPrefix = isAdmin
      ? `${ApiResource.AffiliatePrograms}/${id}/statistics`
      : `${id}/statistics`;
    const resource = isAdmin ? ApiResource.Admin : ApiResource.AffiliatePrograms;

    return apiRequestCall<AffiliateProgramStatistics>({
      params: { cancelToken, method: 'GET', resource, urlPrefix, params },
    });
  }
);

export const updateAffiliateProgram = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/updateAffiliateProgram`,
  (
    { id, maxActivationsNumber, status }: EditAffiliateProgramFormFields & { id: Identifier },
    { signal }
  ): Promise<AffiliateProgramEntity> => {
    const cancelToken = cancelationSubscribe(signal);
    const urlPrefix = `${ApiResource.AffiliatePrograms}/${id}`;
    const data = { maxActivationsNumber, status };
    const resource = ApiResource.Admin;

    return apiRequestCall<AffiliateProgramEntity>({
      params: { method: 'PUT', resource, cancelToken, urlPrefix, data },
      successRequestMessage: `Affiliate code has been successfully updated!`,
    });
  }
);

export const createAffiliateProgram = createAsyncThunk(
  `${EntityStoreName.AffiliatePrograms}/createAffiliateProgram`,
  (data: CreateAffiliateProgramFormFields, { signal }): Promise<AffiliateProgramEntity> => {
    const cancelToken = cancelationSubscribe(signal);
    const urlPrefix = ApiResource.AffiliatePrograms;
    const resource = ApiResource.Admin;

    return apiRequestCall<AffiliateProgramEntity>({
      params: { method: 'POST', resource, urlPrefix, cancelToken, data },
      successRequestMessage: `Affiliate code has been successfully created.`,
    });
  }
);

export const registerReducers = (
  builder: ActionReducerMapBuilder<AffiliateProgramsState>
): void => {
  builder
    .addCase(getAffiliatePrograms.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliatePrograms.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      saveAffiliatePrograms(state, action.payload);
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliatePrograms.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliatePrograms.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliatePrograms.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      saveAffiliatePrograms(state, action.payload);
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliatePrograms.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(createAffiliateProgram.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Creating;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(createAffiliateProgram.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Creating;
      entityAdapter.addOne(state, action.payload);
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(createAffiliateProgram.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Creating;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateAffiliateProgram.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Updating;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateAffiliateProgram.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Updating;
      entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(updateAffiliateProgram.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Updating;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgram.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgram.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      saveAffiliateProgram(state, action.payload);
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgram.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliateProgram.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliateProgram.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      saveAffiliateProgram(state, action.payload);
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAdminAffiliateProgram.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.Loading;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgramStatistics.pending, (state, action) => {
      const status = AffiliateProgramsStoreStatus.StatisticLoading;
      entityStoreStatus.addRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgramStatistics.fulfilled, (state, action) => {
      const status = AffiliateProgramsStoreStatus.StatisticLoading;
      saveAffiliateProgram(state, { statistics: action.payload, id: action.meta.arg.id });
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    })
    .addCase(getAffiliateProgramStatistics.rejected, (state, action) => {
      const status = AffiliateProgramsStoreStatus.StatisticLoading;
      entityStoreStatus.removeRequestId(state, status, action.meta.requestId);
    });
};
